import { api_host } from '../constants'
import { apiFetch } from './apiRequestHandler'

export const getSites = async (archived = false) => {
  return await apiFetch(`${api_host}/sites/list?archived=${archived}`)
}

export const getSiteByUri = async (uri) => {
  return await apiFetch(`${api_host}/sites/by-uri/${uri}`)
}

export const checkSiteUri = async (uri) => {
  return await apiFetch(`${api_host}/sites/check-uri/${uri}`)
}

export const createSite = async (data) => {
  return await apiFetch(`${api_host}/sites/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
}

export const updateSite = async (id, data) => {
  return await apiFetch(`${api_host}/sites/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
}

export const deleteSite = async (id) => {
  return await apiFetch(`${api_host}/sites/${id}`, {
    method: 'DELETE'
  })
}

export const updateSiteVersion = async (uri, oldVersion, newVersion, note) => {
  return await apiFetch(`${api_host}/sites/update-site-version`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      uri, oldVersion, newVersion, note
    })
  })
}

export const getSiteVersions = async (uri) => {
  return await apiFetch(`${api_host}/sites/versions/${uri}`)
}

export const getVersionHistory = async (uri) => {
  return await apiFetch(`${api_host}/sites/version-history/${uri}`)
}

/**
 * Parse a string value to its actual JavaScript type
 * Used for AI-created component data properties
 */
export const parseDataValue = (valueString) => {
  if (valueString === null || valueString === undefined) {
    return valueString;
  }
  
  // If it's not a string, return as-is
  if (typeof valueString !== 'string') {
    return valueString;
  }
  
  const trimmed = valueString.trim();
  
  // Handle boolean
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  
  // Handle null/undefined
  if (trimmed === 'null') return null;
  if (trimmed === 'undefined') return undefined;
  
  // Handle numbers
  if (!isNaN(trimmed) && trimmed !== '') {
    return Number(trimmed);
  }
  
  // Handle strings with quotes (e.g., "''" or "'/path/to/image'")
  if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || 
      (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
    return trimmed.slice(1, -1);
  }
  
  // Parse arrays (normalize single -> double quotes, quote unquoted keys, then parse to actual array)
  // Component code needs arrays, input display will handle stringification
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      // First, quote unquoted object keys (e.g., id: -> "id":)
      let normalized = trimmed.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
      // Then normalize single quotes to double quotes
      normalized = normalized.replace(/'/g, '"');
      return JSON.parse(normalized);
    } catch (e) {
      console.warn('Failed to parse array:', trimmed, e);
      return valueString;
    }
  }

  // Handle objects using Function constructor (safer than eval)
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      return new Function('return ' + trimmed)();
    } catch (e) {
      console.warn('Failed to parse object/array:', trimmed, e);
      return valueString;
    }
  }
  
  // Return as-is if no pattern matches
  return valueString;
};

/**
 * Process components and parse data values for AI-created components
 */
const processAICreatedComponents = (site) => {
  if (!site || !site.components) {
    return site;
  }
  
  site.components = site.components.map(component => {
    // Only parse data for AI-created components
    if (component.created_by_ai === true && component.data && component.data.length > 0) {
      component.data = component.data.map(dataItem => {
        if (dataItem.type === 'const' && dataItem.default_value !== null && dataItem.default_value !== undefined) {
          dataItem.default_value = parseDataValue(dataItem.default_value);
        }
        return dataItem;
      });
    }
    return component;
  });
  
  return site;
};

export const getSiteWithVersion = async (uri, version) => {
  const site = await apiFetch(`${api_host}/sites/admin-by-uri-version/${uri}/${version}`);
  return processAICreatedComponents(site);
}

export const setSiteVersion = async (uri, body) => {
  return await apiFetch(`${api_host}/sites/set-site-version/${uri}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
}

export const deleteSiteVersion = async (uri, version, deleteNote = '') => {
  return await apiFetch(`${api_host}/sites/delete-site-version/${uri}/${version}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ delete_note: deleteNote })
  })
}

export const archiveSite = async (uri) => {
  return await apiFetch(`${api_host}/sites/archive/${uri}`, {
    method: 'DELETE'
  })
}

export const unArchiveSite = async (uri) => {
  return await apiFetch(`${api_host}/sites/unarchive/${uri}`, {
    method: 'DELETE'
  })
}

export const uploadFile = async (file, key = '') => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('key', key)

  return await apiFetch(`${api_host}/upload-file`, {
    method: 'POST',
    body: formData
  })
}

export const getFileLink = async (key) => {
  return await apiFetch(`${api_host}/s3/${key}`)
}

export const duplicateSite = async (source, dest) => {
  return await apiFetch(`${api_host}/sites/duplicate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ source, dest })
  })
}

export const exportSite = async (siteId, versions = null) => {
  let url = `${api_host}/sites/export-site?uri=${siteId}`
  if (versions && versions.length > 0) {
    // Add multiple versions as separate query parameters
    const versionsArray = Array.isArray(versions) ? versions : [versions]
    versionsArray.forEach(version => {
      url += `&versions=${encodeURIComponent(version)}`
    })
  }
  return await apiFetch(url)
}

export const importSite = async (formData) => {
  return await apiFetch(`${api_host}/sites/import-site`, {
    method: 'POST',
    body: formData
  })
}

/**
 * Import one or more versions from an exported JSON file INTO an existing site.
 * `uri` is the target site (the one currently being edited). The FormData is
 * expected to contain: file, versions[], versionMapping (JSON string),
 * and optionally setCurrent / setPublished.
 */
export const importSiteVersion = async (uri, formData) => {
  return await apiFetch(`${api_host}/sites/import-site-version/${uri}`, {
    method: 'POST',
    body: formData
  })
}
// limit is capped at 25 on the server (MAX_PAGE_SIZE)
export const getAuditLogs = async (siteId, { page = 1, limit = 25, action = '', from = '', to = '' } = {}) => {
  const params = new URLSearchParams({ page, limit: Math.min(limit, 25) })
  if (action) params.set('action', action)
  if (from)   params.set('from', from)
  if (to)     params.set('to', to)
  return await apiFetch(`${api_host}/audit-log/${siteId}?${params.toString()}`)
}

export const recordAuditExport = async (siteId) => {
  return await apiFetch(`${api_host}/audit-log/${siteId}/export`, { method: 'POST' })
}
